"use client";
import React, { useEffect, useState } from "react";

interface SmartImageProps {
    id?: string;
    src: string;
    alt: string;
    width?: number;
    height?: number;
    className?: string;
    loading?: "lazy" | "eager";
    preferResize?: boolean;
    onClick?: () => void;
    onLoad?: () => void;
    onError?: () => void;
}

export default function SmartImage({
    id,
    src,
    alt,
    width,
    height,
    className,
    loading = "lazy",
    preferResize,
    onClick,
    onLoad,
    onError,
}: SmartImageProps) {
    const [fallback, setFallback] = useState(false);
    const [mounted, setMounted] = useState(false);
    const base = src.slice(0, src.lastIndexOf(".")) || src;

    useEffect(() => {
        setMounted(true);
    }, [src]);

    const fetchPriority: "high" | "low" = loading === "eager" ? "high" : "low";
    const common = {
        id,
        width,
        height,
        className,
        loading,
        fetchPriority,
        onClick,
        onLoad,
        onError: () => {
            setFallback(true);
            onError?.();
        },
    };

    // After an error, render plain <img> so the browser won’t reselect a broken <source>
    if (fallback) {
        // eslint-disable-next-line @next/next/no-img-element
        return <img src={src} alt={alt} {...common} />;
    }

    // Hydration-safe to make sure React has attached onError handlers
    if (!mounted) {
        // eslint-disable-next-line @next/next/no-img-element
        return <img alt={alt} {...common} />;
    }

    if (preferResize) {
        return (
            <picture>
                <source srcSet={`${base}_resize.avif`} type="image/avif" />
                <source srcSet={`${base}_resize.webp`} type="image/webp" />
                <img src={src} alt={alt} {...common} />
            </picture>
        );
    }

    return (
        <picture>
            <source srcSet={`${base}_resize.avif`} type="image/avif" media="(max-width: 1023px)" />
            <source srcSet={`${base}_resize.webp`} type="image/webp" media="(max-width: 1023px)" />
            <source srcSet={`${base}.avif`} type="image/avif" />
            <source srcSet={`${base}.webp`} type="image/webp" />
            <img src={src} alt={alt} {...common} />
        </picture>
    );
}
